import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import AssetDetailPanel from '@/pages/assets/AssetDetailPanel'
import { AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import { DetailPanelSkeleton } from '@/components/compliance/detail-primitives'
import {
  PageHeaderActions,
  PageHeaderBreadcrumb,
} from '@/components/layout/PageHeaderActions'
import { usePageHeaderTitle } from '@/lib/page-header'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { screenataPageContextAtom } from '@/stores/screenataPageContextStore'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/assets_/$assetId'
)({
  validateSearch: (search: Record<string, unknown>) => ({
    tab:
      typeof search.tab === 'string' &&
      ['details', 'endpoint', 'people', 'compliance', 'sync', 'activity'].includes(
        search.tab
      )
        ? search.tab
        : undefined,
    focus:
      search.focus === 'owner' || search.focus === 'technical-owner'
        ? search.focus
        : undefined,
  }),
  component: AssetDetailPage,
  staticData: { pageHeader: { title: 'Asset Detail', pad: false, scroll: false } },
})

function AssetDetailPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const deleteMutation = useMutation(
    trpc.assetManagement.delete.mutationOptions()
  )
  const markReviewedMutation = useMutation(
    trpc.assetManagement.markReviewed.mutationOptions()
  )
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const { workspaceId, assetId } = useParams({ strict: false }) as {
    workspaceId: string
    assetId: string
  }
  const { canWorkspaceWrite, isAdmin } =
    useWorkspacePermissions(workspaceId)
  const { tab, focus } = Route.useSearch()

  const detailQuery = useQuery(
    trpc.assetManagement.getById.queryOptions(
      { id: assetId ?? '' },
      { enabled: Boolean(assetId) }
    )
  )
  const item = detailQuery.data ?? null

  const goBackToAssets = () => {
    navigate({
      to: '/workspaces/$workspaceId/assets',
      params: { workspaceId },
    })
  }

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.assetManagement.list.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.assetManagement.getStats.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.assetManagement.getSyncStatus.queryKey(),
    })
    if (assetId) {
      queryClient.invalidateQueries({
        queryKey: trpc.assetManagement.getById.queryKey({ id: assetId }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.assetManagement.listExceptions.queryKey({ id: assetId }),
      })
      // Header actions (Mark Reviewed, Decommission) write audit events —
      // keep the Activity tab in step.
      queryClient.invalidateQueries({
        queryKey: trpc.audit.getActivityFeed.infiniteQueryKey({
          targetType: 'asset',
          targetId: assetId,
        }),
      })
    }
  }


  const handleDecommission = async () => {
    if (!canWorkspaceWrite) return
    if (!item) return
    await deleteMutation.mutateAsync({ id: item.id })
    invalidateAll()
    setShowDeleteDialog(false)
    goBackToAssets()
  }

  const handleMarkReviewed = async () => {
    if (!canWorkspaceWrite) return
    if (!item) return
    try {
      await markReviewedMutation.mutateAsync({ id: item.id })
      toast.success('Asset marked reviewed')
      invalidateAll()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to mark reviewed'
      )
    }
  }

  usePageHeaderTitle(item?.name)

  const setPageContext = useSetAtom(screenataPageContextAtom)
  useEffect(() => {
    if (!item) return
    setPageContext({
      page: 'asset-detail',
      summary: `Asset: ${item.name}`,
      data: { assetId: item.id, assetName: item.name },
    })
    return () => setPageContext(null)
  }, [item, setPageContext])

  if (detailQuery.isLoading && !detailQuery.data) {
    return (
      <div className='flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden'>
        <DetailPanelSkeleton />
      </div>
    )
  }

  if (!assetId || detailQuery.isError || !item) {
    return (
      <div className='flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden px-6 py-6'>
        <div className='rounded-sm border border-dashed border-border-divider bg-card px-5 py-10 text-center'>
          <AlertTriangle className='mx-auto mb-3 h-8 w-8 text-text-disabled' />
          <p className='font-body text-[13px] font-medium text-text-body'>
            Asset not found
          </p>
          <p className='font-body mt-1 text-[12px] text-muted-foreground'>
            This asset may have been decommissioned or is no longer available.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden'>
      <PageHeaderBreadcrumb>
        <button
          type='button'
          onClick={goBackToAssets}
          className='font-heading text-[15px] font-medium text-text-subtle hover:text-cobalt-strong'
        >
          Assets
        </button>
      </PageHeaderBreadcrumb>
      {canWorkspaceWrite && (
        <PageHeaderActions>
          <Button
            variant='outline'
            size='sm'
            className='h-8 rounded-sm px-3 text-[12px]'
            onClick={() => setShowDeleteDialog(true)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Decommissioning...' : 'Decommission'}
          </Button>
          <Button
            size='sm'
            className='h-8 rounded-sm px-3 text-[12px]'
            onClick={handleMarkReviewed}
            disabled={markReviewedMutation.isPending}
          >
            {markReviewedMutation.isPending ? 'Reviewing...' : 'Mark Reviewed'}
          </Button>
        </PageHeaderActions>
      )}
      <AssetDetailPanel
        item={item}
        onUpdate={invalidateAll}
        readOnly={!canWorkspaceWrite}
        canManageOwnership={isAdmin}
        focusTarget={focus}
        tab={tab ?? 'details'}
        onTabChange={(next) =>
          navigate({
            to: '/workspaces/$workspaceId/assets/$assetId',
            params: { workspaceId, assetId },
            search: { tab: next, focus: undefined },
            replace: true,
          })
        }
      />
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title='Decommission asset?'
        desc={`Mark ${item.name} as decommissioned. It'll stay in the register for audit trail but won't count toward stats.`}
        confirmText='Decommission'
        destructive
        isLoading={deleteMutation.isPending}
        handleConfirm={() => {
          void handleDecommission()
        }}
      />
    </div>
  )
}
